/* eslint-disable react/jsx-props-no-spreading */
import { useCallback, useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { VENDORED_DEPENDENCY_DETECTION } from '../../../../../shared/featureFlags/flagNames';
import { Category, IssueScopeKind } from '../../../../../shared/issuesV2';
import { window } from '../../../../globals';
import LabeledCheckbox from '../../../generic/Forms/LabeledCheckbox';
import FeatureFlagRequired from '../../../generic/Gates/FeatureFlagRequired';
import QualityRequired from '../../../generic/Gates/QualityRequired';
import IssuesSection from '../../../shared/settings/SettingsPageIssueSection';
import Section from '../../../shared/settings/SettingsPageSection';

import PropagateModal from './PropagateIssuesModal';
import useIssueSettings from './useIssueSettings';
import type { IssueSettingsProps } from './types';
import type { GenericIssueSettingsFields } from './utils';

/**
 * SettingsOrganizationProjectIssuesQuality is the settings page for
 * managing the default quality issue settings for projects.
 */
const SettingsOrganizationProjectIssuesQuality: VoidFunctionComponent<IssueSettingsProps> = ({
  policies: policyOptions,
}) => {
  const [isPropagateModalOpen, setIsPropagateModalOpen] = useState<boolean>(false);
  const {
    settings,
    isLoading,
    onUpdateSettings,
    policies,
    filters,
    isSaveButtonDisabled,
    onSaveSettings,
  } = useIssueSettings(
    window.user?.organizationId as number,
    Category.Quality,
    policyOptions,
  );
  const qualitySettings = settings;

  const renderVendoredPropagateSettings = useCallback(
    ({ additionalFields, setAdditionalFields }) => {
      return (
        <FeatureFlagRequired flagName={VENDORED_DEPENDENCY_DETECTION}>
          <LabeledCheckbox
            label={
            qualitySettings?.isVendoredScanningEnabled
              ? `Enable vendored dependency issue scanning for all projects`
              : `Disable vendored dependency issue scanning for all projects`
          }
            isChecked={additionalFields.includes('projectDefaultVendoredQualityIssueScanningEnabled')}
            onChange={() =>
              setAdditionalFields((prev: GenericIssueSettingsFields[]) =>
                prev.includes('projectDefaultVendoredQualityIssueScanningEnabled')
                  ? prev.filter((field) => field !== 'projectDefaultVendoredQualityIssueScanningEnabled')
                  : [...prev, 'projectDefaultVendoredQualityIssueScanningEnabled'],
              )}
            isRippleDisabled
          />
        </FeatureFlagRequired>
      );
    }, [qualitySettings]);

  return (
    <>
      <Section>
        <Section.Left heading="Quality">
          <Section.Paragraph>Default configuration for quality issues on new projects</Section.Paragraph>
          <Section.Buttons>
            <Section.PropagateButton onClick={() => setIsPropagateModalOpen(true)} isDisabled={!isSaveButtonDisabled} />
            <Section.SaveButton onClick={onSaveSettings} isLoading={isLoading.save} isDisabled={isSaveButtonDisabled} />
          </Section.Buttons>
        </Section.Left>
        <Section.Right>
          <IssuesSection
            category={Category.Quality}
            scopeType={IssueScopeKind.Global}
            isScanningEnabled={settings?.isScanningEnabled || false}
            onToggleIsScanningEnabled={(isScanningEnabled) => onUpdateSettings({
              isScanningEnabled,
              ...(!isScanningEnabled && { isVendoredScanningEnabled: false }),
            })}
            isCICheckEnabled={settings?.isCICheckEnabled || false}
            onToggleIsCICheckEnabled={(isCICheckEnabled) => onUpdateSettings({ isCICheckEnabled })}
            policyOptions={policies.policyOptions}
            selectedPolicyOption={policies.selectedPolicyOption}
            onSelectedPolicyChange={(policyId) => onUpdateSettings({ policyId })}
            filterOptions={filters.filterOptions}
            selectedFilterOption={filters.selectedFilterOption}
            onSelectedFilterChange={(filterId) => onUpdateSettings({ filterId })}
            isVendoredScanningEnabled={qualitySettings?.isVendoredScanningEnabled ?? false}
            onToggleVendoredScanning={(isChecked) => onUpdateSettings({ isVendoredScanningEnabled: isChecked })}
            // Users with organization settings permissions can edit the issue policy and filters
            canEditFilters
            canEditPolicy
          />
        </Section.Right>
      </Section>
      {settings && (
        <PropagateModal
          category={Category.Quality}
          isOpen={isPropagateModalOpen}
          onHide={() => setIsPropagateModalOpen(false)}
          policyName={policies.selectedPolicyName}
          filterName={filters.selectedFilterName}
          settings={settings}
          renderVendoredSettings={renderVendoredPropagateSettings}
        />
      )}
    </>
  );
};

/**
 * Container which gates the Quality settings page behind the
 * QualityRequired component, which checks `organization.quality_policy_enabled`
 */
const SettingsOrganizationProjectIssuesQualityGate: VoidFunctionComponent<IssueSettingsProps> = (props) => (
  <QualityRequired>
    <SettingsOrganizationProjectIssuesQuality {...props} />
  </QualityRequired>
);

export default SettingsOrganizationProjectIssuesQualityGate;
